import React, { useState, useEffect } from 'react';
import { PopUp } from '@/components/design-system/molecules/pop-up';
import { Modal } from '@/components/design-system/atoms/modal';
import { TextInput } from '@/components/design-system/atoms/text/text-input';
import { OptionStateSelector } from '@/components/design-system/atoms/option-state-selector';
import { DropDown } from '@/components/design-system/molecules/dropdown';
import { Button } from '@/components/design-system/atoms/button';
import { RichTextEditor } from '@/components/design-system/molecules/rich-text-editor';
import { IconSaxIcon } from '@/components/design-system/atoms/icon/iconsax-icon';
import type { DiscountFormData } from '../../types/discount';
import FileUpload from '@/components/FileUpload';
import { ImageUpload } from '@/components/design-system/molecules/image-upload';

const typeOptions = [
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'toilet_paper', label: 'Toilet paper' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'other', label: 'Other' },
];

const periodOptions = [
  { value: 'this_month', label: 'This month' },
  { value: 'next_month', label: 'Next month' },
  { value: 'recurring', label: 'Recurring' },
];

export type EditDiscountModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: DiscountFormData) => void;
  discount: any; // The discount object to edit
};

const EditDiscountModal: React.FC<EditDiscountModalProps> = ({
  open,
  onClose,
  onSubmit,
  discount,
}) => {
  const [amount, setAmount] = useState<number | ''>('');
  const [unit, setUnit] = useState('€');
  const [discountType, setDiscountType] = useState('');
  const [period, setPeriod] = useState('this_month');
  const [note, setNote] = useState('');
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const [removingAttachmentIds, setRemovingAttachmentIds] = useState<number[]>([]);

  useEffect(() => {
    if (open && discount) {
      setAmount(discount.amount_cents ? discount.amount_cents / 100 : '');
      setUnit(discount.measurement === 'percent' ? '%' : '€');
      setDiscountType(discount.discount_category || '');
      setPeriod(discount.period || 'this_month');
      setNote(discount.note || '');
      setStartDate(discount.start_date ? discount.start_date.substring(0, 10) : undefined);
      setEndDate(discount.end_date ? discount.end_date.substring(0, 10) : undefined);
      setExistingAttachments(discount.attachments || []);
      setAttachments(discount?.attachments || []);
      setRemovingAttachmentIds([]);
    } else if (!open) {
      // Optionally reset all fields when modal is closed, if desired behavior
      // setAmount(''); setUnit('€'); setDiscountType(''); etc.
    }
  }, [open, discount]);

  if (!open) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const handleRemoveNewAttachment = (fileName: string) => {
    setAttachments((prev) => prev.filter((file) => file.name !== fileName));
  };

  const handleRemoveExistingAttachment = (attachmentToRemove: any) => {
    if (attachmentToRemove.id) {
      setRemovingAttachmentIds((prev) => [...prev, attachmentToRemove.id]);
    }
    setExistingAttachments((prev) => prev.filter((att) => att !== attachmentToRemove));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (amount === '') return;

    onSubmit({
      amount: amount as number,
      unit: unit as '€' | '%',
      discount_category: Number(discountType),
      period: period as 'this_month' | 'next_month' | 'recurring',
      note,
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined,
      attachments,
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <PopUp
        type="default"
        title="Edit Discount"
        primaryButtonProps={{
          children: 'Update Discount',
          type: 'submit',
          onClick: handleSubmit,
          className: 'flex-1',
        }}
        secondaryButtonProps={{
          children: 'Cancel',
          type: 'button',
          onClick: onClose,
          className: 'flex-1',
          variant: 'ghost',
        }}
        buttonOrder="primary-first"
      >
        <PopUp.PopUpInput>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex gap-3">
              <TextInput
                label="Amount"
                type="number"
                step="0.01"
                min="0"
                value={amount === '' ? undefined : amount}
                onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="€0.00"
                required
                className="flex-1"
              />
              <OptionStateSelector
                options={[
                  { value: '€', label: '€' },
                  { value: '%', label: '%' },
                ]}
                selectedOption={unit}
                onSelect={setUnit}
                btnClassName="mt-[7px]"
              />
            </div>
            <div className="flex gap-2">
              <DropDown
                label="Type"
                value={discountType}
                onChange={setDiscountType}
                placeholder="Select"
                className="flex-1"
              >
                <DropDown.DropDownOptions>
                  {typeOptions.map((opt) => (
                    <DropDown.DropDownOption key={opt.value} value={opt.value}>
                      {opt.label}
                    </DropDown.DropDownOption>
                  ))}
                </DropDown.DropDownOptions>
              </DropDown>
              <DropDown
                label="Period"
                value={period}
                onChange={setPeriod}
                placeholder="Select"
                className="flex-1"
              >
                <DropDown.DropDownOptions>
                  {periodOptions.map((opt) => (
                    <DropDown.DropDownOption key={opt.value} value={opt.value}>
                      {opt.label}
                    </DropDown.DropDownOption>
                  ))}
                </DropDown.DropDownOptions>
              </DropDown>
            </div>
            {period === 'recurring' && (
              <div className="flex gap-2">
                <input
                  type="date"
                  value={startDate || ''} // Already YYYY-MM-DD from useEffect
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder="Start date"
                  className="flex-1 rounded border px-3 py-2"
                />
                <input
                  type="date"
                  value={endDate || ''} // Already YYYY-MM-DD from useEffect
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder="End date"
                  className="flex-1 rounded border px-3 py-2"
                />
              </div>
            )}
            <RichTextEditor
              value={note}
              onChange={setNote}
              label="Note (Optional)"
              className="w-full"
              maxRichTextLength={200}
            />
            <FileUpload
              attachments={attachments}
              handleFileChange={handleFileChange}
              handleRemoveAttachment={handleRemoveNewAttachment}
            />
            <ImageUpload
              label="Image"
              onImageUpload={(file) => {
                console.log(file);
                setAttachments([file as File]);
              }}
              maxSize={5242880}
              acceptedFileTypes={['image/png', 'image/svg', 'image/jpg', 'image/jpeg']}
              onRemoveImage={() => {
                setAttachments([]);
              }}
            />
          </form>
        </PopUp.PopUpInput>
      </PopUp>
    </Modal>
  );
};

export default EditDiscountModal;
